import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { DeleteOutlineOutlined, PersonOutlined, SwapHorizOutlined } from '@mui/icons-material'
import { FC, useMemo, useState } from 'react'
import { WorkspaceInvitationState, WorkspaceSeatType } from 'models/user'

import { GRAY_700 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { ReassignBrokerPopup } from '../ReassignBrokerPopup/ReassignBrokerPopup.component'
import { Stack } from '@mui/material'
import { TransferPropertiesPopup } from '../TransferPropertiesPopup/TransferPropertiesPopup.component'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'
import { useActionPopup } from 'utils/hooks/useActionPopup'
import { useBulkDeleteProperties } from 'dataQueries'
import { useCanBulkActOnProperties } from '../contexts'
import { usePropertiesDashboard } from '../contexts/PropertiesDashboard.context'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceList } from 'utils/hooks/useWorkspaceList'

interface Props {
  /** Email of the broker shared by all currently selected properties, if any. Forwarded to the reassign popup to flag the already-assigned broker. */
  currentBrokerEmail?: string
}

export const PropertiesDashboardBulkActions: FC<Props> = ({ currentBrokerEmail }) => {
  const { t } = useTranslation(['properties_dashboard', 'actions'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { showConfirm } = useActionPopup()
  const { selectedIds, selectedCount, clearSelection } = usePropertiesDashboard()
  const { userPropertyWorkspaceId } = useUserData()
  const { activeWorkspaceList } = useWorkspaceList()
  const deleteMutation = useBulkDeleteProperties()
  const showBulkActions = useCanBulkActOnProperties()

  const [isTransferOpen, setIsTransferOpen] = useState(false)
  const [isReassignBrokerOpen, setIsReassignBrokerOpen] = useState(false)

  const realtyIds = Array.from(selectedIds)

  // Backend requires at least RESTRICTED access in the target workspace; SPONSORED seats are rejected with 403
  const hasTransferableWorkspaces = useMemo(() =>
    activeWorkspaceList.some(ws =>
      ws.id !== userPropertyWorkspaceId
      && ws.invitationState !== WorkspaceInvitationState.PENDING
      && [WorkspaceSeatType.RESTRICTED, WorkspaceSeatType.FULL].includes(ws.seatType)
    )
    , [activeWorkspaceList, userPropertyWorkspaceId])

  if (!showBulkActions || selectedCount === 0) return null

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      t('bulk_actions.delete_confirm_body', { count: selectedCount }),
      {
        title: t('bulk_actions.delete_confirm_title'),
        confirmAcceptText: t('bulk_actions.delete'),
        confirmDenyText: t('actions:Cancel'),
      }
    )
    if (!confirmed) return

    deleteMutation.mutate({
      workspaceId: userPropertyWorkspaceId,
      realtyIds,
    }, {
      onSuccess: () => {
        logAnalyticsEvent(AnalyticsEvent.BULK_DELETE_PROPERTIES, { count: selectedCount })
        spawnSuccessToast(t('bulk_actions.delete_success', { count: selectedCount }))
        clearSelection()
      },
      onError: (error) => {
        logger.error('Bulk delete failed', error as Error, {
          context: { component: 'PropertiesDashboardBulkActions', action: 'bulk_delete' }
        })
        spawnErrorToast(t('bulk_actions.delete_error'))
      }
    })
  }

  const isPending = deleteMutation.isPending

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap="1.6rem"
      marginBottom="1.6rem"
    >

      <Typography variant="text-sm" fontWeight={600} color={GRAY_700}>
        {t('bulk_actions.selected_count', { count: selectedCount })}
      </Typography>

      <Stack flexDirection="row" gap=".8rem">

        {hasTransferableWorkspaces && (
          <MUIButton
            type="secondaryBorder"
            disabled={isPending}
            onClick={() => setIsTransferOpen(true)}
          >
            <Stack flexDirection="row" alignItems="center" gap=".8rem">

              <SwapHorizOutlined fontSize="inherit" />

              {t('bulk_actions.transfer')}

            </Stack>
          </MUIButton>
        )}

        <MUIButton
          type="secondaryBorder"
          disabled={isPending}
          onClick={() => setIsReassignBrokerOpen(true)}
        >
          <Stack flexDirection="row" alignItems="center" gap=".8rem">

            <PersonOutlined fontSize="inherit" />

            {t('bulk_actions.update_broker')}

          </Stack>
        </MUIButton>

        <MUIButton
          type="orangeSecondaryBorder"
          disabled={isPending}
          isLoading={isPending}
          onClick={handleDelete}
        >
          <Stack flexDirection="row" alignItems="center" gap=".8rem">

            <DeleteOutlineOutlined fontSize="inherit" />

            {t('bulk_actions.delete')}

          </Stack>
        </MUIButton>

      </Stack>

      <TransferPropertiesPopup
        isOpen={isTransferOpen}
        realtyIds={realtyIds}
        onClose={() => setIsTransferOpen(false)}
        onSuccess={() => {
          setIsTransferOpen(false)
          clearSelection()
        }}
      />

      <ReassignBrokerPopup
        isOpen={isReassignBrokerOpen}
        realtyIds={realtyIds}
        currentBrokerEmail={currentBrokerEmail}
        onClose={() => setIsReassignBrokerOpen(false)}
        onSuccess={() => {
          setIsReassignBrokerOpen(false)
          clearSelection()
        }}
      />

    </Stack>
  )
}
